/**
 * Structured address helpers. formatAddress mirrors
 * HHNodeServer/src/utils/nurseryAddress.js, which writes the one-line legacy
 * columns; both test suites pin the same cases.
 */

export const AU_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

export interface AddressParts {
  line1?: string | null;
  line2?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  plus_code?: string | null;
}

const clean = (v: unknown) => (v == null ? '' : String(v).replace(/\s+/g, ' ').trim());

/** "12 Smith St, Unit 4, Shepparton VIC 3630" — Australia is implied. */
export function formatAddress(a: AddressParts | null | undefined): string {
  if (!a) return '';
  const locality = [clean(a.suburb), clean(a.state), clean(a.postcode)].filter(Boolean).join(' ');
  const parts = [clean(a.line1), clean(a.line2), locality].filter(Boolean);
  const country = clean(a.country);
  if (country && country.toLowerCase() !== 'australia') parts.push(country);
  if (!clean(a.line1) && clean(a.plus_code)) parts.unshift(clean(a.plus_code));
  return parts.join(', ');
}

// Open Location Code alphabet: 2-9 plus CFGHJMPQRVWX. A full code is 8
// characters, '+', then 2-3; a short code drops 2-6 leading characters and is
// usually followed by a locality ("4RJ7+2V Tatura VIC").
const PLUS_CODE = /^([2-9CFGHJMPQRVWX]{2,8})\+([2-9CFGHJMPQRVWX]{0,3})(?:[\s,]+(.+))?$/i;

export function isPlusCode(value: string | null | undefined): boolean {
  const v = clean(value);
  return v !== '' && PLUS_CODE.test(v);
}

/**
 * What to hand Google Maps for this address. A plus code wins, since it is
 * there precisely because the street address does not find the spot. A short
 * code with no locality of its own borrows the address's suburb, which is
 * what Google needs to resolve it.
 */
export function mapsQuery(a: AddressParts): string {
  const code = clean(a.plus_code);
  const m = code.match(PLUS_CODE);
  if (m) {
    const isShort = m[1].length < 8;
    if (isShort && !m[3]) {
      const locality = [clean(a.suburb), clean(a.state)].filter(Boolean).join(' ');
      return [`${m[1]}+${m[2]}`.toUpperCase(), locality].filter(Boolean).join(' ');
    }
    return code;
  }
  return formatAddress({ ...a, plus_code: null });
}

export function mapsUrl(a: AddressParts): string | null {
  const q = mapsQuery(a);
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
}
